import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AgentConfig } from '../../types/agent.types';
import { fetchManifests } from '../../api/manifestApi';

export interface AgentState {
  activeAgent: AgentConfig | null;
  isTokenAcquired: boolean;
  tokenError: string | null;
  agents: AgentConfig[];
  agentsLoading: boolean;
  agentsError: string | null;
}

const initialState: AgentState = {
  activeAgent: null,
  isTokenAcquired: false,
  tokenError: null,
  agents: [],
  agentsLoading: false,
  agentsError: null,
};

/** Fetch the list of agents from the manifest API. */
export const fetchAgentsThunk = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('agent/fetchAgents', async (_, { dispatch, rejectWithValue }) => {
  try {
    dispatch(agentSlice.actions.clearAgents());
    await fetchManifests((agent) => {
      dispatch(agentSlice.actions.addAgent(agent));
    });
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to fetch agents';
    return rejectWithValue(message);
  }
});

const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    addAgent: (state, action: PayloadAction<AgentConfig>) => {
      if (!state.agents.find(a => a.id === action.payload.id)) {
        state.agents.push(action.payload);
      }
    },
    clearAgents: (state) => {
      state.agents = [];
    },
    setActiveAgent: (state, action: PayloadAction<AgentConfig>) => {
      state.activeAgent = action.payload;
      state.isTokenAcquired = false;
      state.tokenError = null;
    },
    clearActiveAgent: (state) => {
      state.activeAgent = null;
      state.isTokenAcquired = false;
      state.tokenError = null;
    },
    tokenAcquired: (state) => {
      state.isTokenAcquired = true;
      state.tokenError = null;
    },
    tokenAcquisitionFailed: (state, action: PayloadAction<string>) => {
      state.isTokenAcquired = false;
      state.tokenError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAgentsThunk.pending, (state) => {
      state.agentsLoading = true;
      state.agentsError = null;
    });
    builder.addCase(fetchAgentsThunk.fulfilled, (state) => {
      state.agentsLoading = false;
    });
    builder.addCase(fetchAgentsThunk.rejected, (state, action) => {
      state.agentsLoading = false;
      state.agentsError = action.payload ?? 'Failed to fetch agents';
    });
  },
});

export const {
  addAgent,
  clearAgents,
  setActiveAgent,
  clearActiveAgent,
  tokenAcquired,
  tokenAcquisitionFailed,
} = agentSlice.actions;

export default agentSlice.reducer;
